import React, { useState } from 'react';
import {
  FileText,
  Sparkles,
  Printer,
  CheckCircle2,
  Lock,
  Stethoscope,
  Pill,
  BookOpen,
  ClipboardList,
  RefreshCw,
  Send
} from 'lucide-react';
import { Patient, ClinicalDocument } from '../types';
import { generateClinicalDocument } from '../services/geminiService';
import { NativeMarkdownViewer } from './NativeMarkdownViewer';

interface ClinicalDocsProps {
  patients: Patient[];
  selectedPatient: Patient;
  onSelectPatient: (patient: Patient) => void;
  activeDoc: ClinicalDocument;
  onUpdateDoc: (doc: ClinicalDocument) => void;
  onOpenPrintPreview: (doc: ClinicalDocument) => void;
}

export const ClinicalDocs: React.FC<ClinicalDocsProps> = ({
  patients,
  selectedPatient,
  onSelectPatient,
  activeDoc,
  onUpdateDoc,
  onOpenPrintPreview
}) => {
  const [docType, setDocType] = useState<ClinicalDocument['docType']>(activeDoc.docType);
  const [clinicianNotes, setClinicianNotes] = useState<string>(
    `Patient evaluated in ICU bed. Complains of persistent retrosternal pressure. ECG reveals ongoing ST changes in inferior leads. Troponin trending upward. Recommend stat heparin protocol and emergent cath lab activation.`
  );
  const [isSynthesizing, setIsSynthesizing] = useState<boolean>(false);
  const [isSigning, setIsSigning] = useState<boolean>(false);

  const handleGenerateAI = async () => {
    setIsSynthesizing(true);
    const result = await generateClinicalDocument(selectedPatient, clinicianNotes, docType);

    const updated: ClinicalDocument = {
      ...activeDoc,
      patientId: selectedPatient.id,
      patientName: selectedPatient.fullName,
      mrn: selectedPatient.mrn,
      docType: docType,
      date: new Date().toLocaleString() + ' EST',
      chiefComplaint: result.chiefComplaint || activeDoc.chiefComplaint,
      subjective: result.subjective || activeDoc.subjective,
      objective: result.objective || activeDoc.objective,
      assessment: result.assessment || activeDoc.assessment,
      plan: result.plan || activeDoc.plan,
      icd10Codes: result.icd10Codes || activeDoc.icd10Codes,
      prescriptions: result.prescriptions || activeDoc.prescriptions,
      aiSynthesized: true,
      signed: false
    };

    onUpdateDoc(updated);
    setIsSynthesizing(false);
  };

  const handleSignDocument = () => {
    setIsSigning(true);
    setTimeout(() => {
      const now = new Date().toISOString();
      const signedDoc: ClinicalDocument = {
        ...activeDoc,
        signed: true,
        signatureDate: now,
        verificationHash: `SHA256:${Math.random().toString(16).substring(2, 10)}${Math.random().toString(16).substring(2, 10)}${Math.random().toString(16).substring(2, 10)}`
      };
      onUpdateDoc(signedDoc);
      setIsSigning(false);
    }, 600);
  };

  return (
    <div className="space-y-6">
      {/* Top Patient Selector Bar */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="p-2.5 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono text-slate-400">Target Patient Encounter</label>
            <select
              value={selectedPatient.id}
              onChange={(e) => {
                const found = patients.find((p) => p.id === e.target.value);
                if (found) onSelectPatient(found);
              }}
              className="bg-slate-950 border border-slate-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-500 w-full md:w-64"
            >
              {patients.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.fullName} ({pt.mrn}) — {pt.department}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <div className="flex items-center gap-2">
            {(['SOAP Note', 'Discharge Summary', 'Consultation', 'Surgical Brief'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setDocType(type)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${
                  docType === type
                    ? 'bg-teal-500 text-slate-950 font-bold'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <button
            onClick={() => onOpenPrintPreview(activeDoc)}
            className="flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg transition"
          >
            <Printer className="h-4 w-4 text-teal-400" />
            <span className="font-semibold">Print Preview</span>
          </button>
        </div>
      </div>

      {/* Main Two-Column Layout: Clinician Input & AI Synthesized Document */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Clinician Audio / Text Input */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-teal-400" />
                <span>Clinician Encounter Input</span>
              </h3>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">
                AI SCRIBE INPUT
              </span>
            </div>

            <p className="text-xs text-slate-400">
              Dictate or type unstructured clinical findings, bedside impressions, and rapid lab results.
            </p>

            <textarea
              rows={8}
              value={clinicianNotes}
              onChange={(e) => setClinicianNotes(e.target.value)}
              placeholder="Enter patient symptoms, physical exam telemetry, or treatment response..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 font-sans leading-relaxed"
            />

            <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800/80 text-xs space-y-2">
              <div className="text-[11px] font-mono font-semibold text-slate-400">Patient Baseline Vitals Feed</div>
              <div className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                <div className="text-slate-300">HR: <span className="text-teal-400">{selectedPatient.vitals.heartRate}</span> bpm</div>
                <div className="text-slate-300">BP: <span className="text-teal-400">{selectedPatient.vitals.bpSystolic}/{selectedPatient.vitals.bpDiastolic}</span></div>
                <div className="text-slate-300">SpO2: <span className="text-teal-400">{selectedPatient.vitals.spO2}%</span></div>
              </div>
            </div>

            <button
              onClick={handleGenerateAI}
              disabled={isSynthesizing || !clinicianNotes.trim()}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 font-bold py-3 px-4 rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 disabled:opacity-50"
            >
              <Sparkles className={`h-4 w-4 ${isSynthesizing ? 'animate-spin' : ''}`} />
              <span>{isSynthesizing ? 'Synthesizing with Gemini 2.5 Flash...' : 'Synthesize Certified EHR Note'}</span>
            </button>
          </div>

          {/* ICD-10 & Medication Quick Codes */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3">
            <h4 className="text-xs font-bold text-slate-200 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-cyan-400" />
              <span>Diagnostic ICD-10 Codes Associated</span>
            </h4>
            <div className="space-y-1.5">
              {activeDoc.icd10Codes.map((icd, idx) => (
                <div key={idx} className="flex items-start justify-between p-2 rounded-lg bg-slate-950/70 border border-slate-800 text-xs">
                  <span className="font-mono font-bold text-teal-400 bg-teal-950/50 px-1.5 py-0.5 rounded border border-teal-800/40">
                    {icd.code}
                  </span>
                  <span className="text-slate-300 text-[11px] text-right pl-3">{icd.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Certified EHR Document Preview */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl relative">
            {/* Document Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-800 gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-extrabold text-white tracking-wide">
                    {activeDoc.docType.toUpperCase()}
                  </h3>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/10 text-teal-300 border border-teal-500/30">
                    {activeDoc.id}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Patient: <strong className="text-slate-200">{activeDoc.patientName}</strong> (MRN: {activeDoc.mrn})
                </p>
              </div>

              <div className="flex items-center gap-2">
                {activeDoc.signed ? (
                  <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs px-3 py-1.5 rounded-lg">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-semibold">Digitally Signed & Locked</span>
                  </div>
                ) : (
                  <button
                    onClick={handleSignDocument}
                    disabled={isSigning}
                    className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition"
                  >
                    <Lock className="h-3.5 w-3.5" />
                    <span>{isSigning ? 'Hashing Certificate...' : 'Attest & Sign Note'}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Document Body Sections */}
            <div className="mt-5 space-y-4 text-xs">
              {/* Chief Complaint */}
              <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                <span className="text-[10px] font-mono uppercase text-teal-400 tracking-wider">Chief Complaint</span>
                <p className="font-semibold text-slate-200 text-sm mt-0.5">{activeDoc.chiefComplaint}</p>
              </div>

              {/* Subjective */}
              <div className="p-3.5 rounded-xl bg-slate-950/40 border border-slate-800/80">
                <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block mb-1">
                  1. Subjective (History of Present Illness)
                </span>
                <NativeMarkdownViewer content={activeDoc.subjective} />
              </div>

              {/* Objective */}
              <div className="p-3.5 rounded-xl bg-slate-950/40 border border-slate-800/80">
                <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block mb-1">
                  2. Objective (Physical Examination & Telemetry)
                </span>
                <NativeMarkdownViewer content={activeDoc.objective} />
              </div>

              {/* Assessment */}
              <div className="p-3.5 rounded-xl bg-slate-950/40 border border-slate-800/80">
                <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block mb-1">
                  3. Assessment & Clinical Differential
                </span>
                <NativeMarkdownViewer content={activeDoc.assessment} />
              </div>

              {/* Plan */}
              <div className="p-3.5 rounded-xl bg-slate-950/40 border border-slate-800/80">
                <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block mb-1">
                  4. Plan & Interventional Management
                </span>
                <NativeMarkdownViewer content={activeDoc.plan} />
              </div>

              {/* Prescriptions Section */}
              {activeDoc.prescriptions.length > 0 && (
                <div className="p-3.5 rounded-xl bg-slate-950/40 border border-slate-800/80">
                  <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block mb-2 flex items-center gap-1.5">
                    <Pill className="h-3.5 w-3.5 text-teal-400" />
                    <span>Medication Administration Orders</span>
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {activeDoc.prescriptions.map((rx, idx) => (
                      <div key={idx} className="p-2 rounded-lg bg-slate-900 border border-slate-800 flex flex-col justify-between">
                        <span className="font-semibold text-slate-100">{rx.drug}</span>
                        <div className="text-[11px] text-teal-300 font-mono mt-1">
                          {rx.dosage} • {rx.frequency} ({rx.route})
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Signature Stamp & Audit Footer */}
            <div className="mt-6 pt-4 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-slate-400 gap-2">
              <div>
                <p>Attending: <strong className="text-slate-200">{activeDoc.author}</strong></p>
                {activeDoc.signatureDate && (
                  <p className="font-mono text-[10px] text-emerald-400">Signed: {activeDoc.signatureDate}</p>
                )}
              </div>

              {activeDoc.verificationHash && (
                <div className="font-mono text-[10px] text-slate-500 max-w-xs truncate text-right">
                  Seal: {activeDoc.verificationHash}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
